import './App.scss';
import { useState, useEffect, useRef } from 'react';
import Confetti from 'react-confetti';
import ReactPlayer from 'react-player/youtube';
import StandardButton from './reusable-components/buttons/standard-button';
import InputButton from './reusable-components/buttons/input-button';
import MysteryWord from './reusable-components/mystery-word/mystery-word';
import HPBar from './reusable-components/hp-bar/hp-bar';
import { formatTime } from './util';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent } from 'firebase/analytics';
import useCountdownTimer from './custom-hooks/countdown-timer';
import SeekingOverlay from './reusable-components/seeking-overlay/seeking-overlay';
import TitleScreen from './screens/title-screen/title-screen';
import GameOverScreen from './screens/game-over-screen/game-over-screen';
import RoundOverScreen from './screens/round-over-screen/round-over-screen';
import SearchingScreen from './screens/searching-screen/searching-screen';
import TutorialScreen from './screens/tutorial-screen/tutorial-screen';
import CountUp from 'react-countup';
import useWindowSize from 'react-use/lib/useWindowSize';
import test from './img/test.mp4'

const firebaseConfig = {
  apiKey: "AIzaSyCI5pnnoKyC9g1AHSN0qq_xkGc3bOaUSPk",
  authDomain: "word-player-sweeny.firebaseapp.com",
  projectId: "word-player-sweeny",
  storageBucket: "word-player-sweeny.appspot.com",
  messagingSenderId: "904947896904",
  appId: "1:904947896904:web:0a2085a0fed5025e153e7d",
  measurementId: "G-ZHFWCJ21E1"
};
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const searchYoutube = httpsCallable(getFunctions(), 'searchYoutube');

const mysteryWordsFile = require('./words.txt');
const maxHP: number = 100;
const timeLimit: number = 120000;

function App() {
  const [gameState, setGameState] = useState<GameState>('menu');
  const [gameMode, setGameMode] = useState<GameMode>('endurance');
  const [currentScore, setCurrentScore] = useState<number>(0);
  const [roundScores, setRoundScores] = useState<RoundScores>({independence: 100, certainty: 100});
  const [averageRoundScores, setAverageRoundScores] = useState<RoundScores>({independence: 0, certainty: 0});
  const [roundNumber, setRoundNumber] = useState<number>(0);
  const [hintPoints, setHintPoints] = useState<number>(maxHP);
  const [userWord, setUserWord] = useState<string>('');
  const [mysteryWord, setMysteryWord] = useState<string>('');
  const [guessedWords, setGuessedWords] = useState<Array<string>>([]);
  const [videosPurchased, setVideosPurchased] = useState<number>(0);
  const [searchIndex, setSearchIndex] = useState<number>(0);
  const [searchInProgress, setSearchInProgress] = useState<boolean>(false);
  const [searchErrorMessage, setSearchErrorMessage] = useState<string>('');
  const [videoId, setVideoId] = useState<string>();
  const [videoIsPlaying, setVideoIsPlaying] = useState<boolean>(false);
  const [videoDuration, setVideoDuration] = useState<number>(0);  
  const [videoHistory, setVideoHistory] = useState<Array<Array<VideoInfo>>>([]);
  const [confettiFalling, setConfettiFalling] = useState<boolean>(false);
  //const [backgroundPosition, setBackgroundPosition] = useState<Array<number>>([0,0]);
  const wordsList = useRef<Array<string>>([]);
  const currentSearch = useRef<Array<VideoInfo>>([]);
  const mysteryWordComponentRef = useRef<any>(null);
  const videoPlayerComponentRef = useRef<any>(null);
  const [timeRemaining, toggleTimer, setTimeRemaining] = useCountdownTimer(timeLimit);
  const { width, height } = useWindowSize();

  /** Prepare the words list. This code will only run when component mounts */
  useEffect(() => {
    fetch(mysteryWordsFile).then(response => response.text()).then((text) => {
      wordsList.current = text.split('\r\n');
    });
  }, []);

  /** Run searchVids everytime there is a change to userWord or mysteryWord to generate and select new videos */
  useEffect(() => {
    const searchVids = async() => {
      setSearchErrorMessage(``);
      currentSearch.current = [];
      setVideoIsPlaying(false);
      toggleTimer(false);
      setSearchInProgress(true);
      searchYoutube(`${userWord} ${mysteryWord}`).then(async (searchResults: any) => {
        if (searchResults.data.length === 0) {
          setSearchErrorMessage(`No results were found for that search, let's try something else!`);
        } else {
          const newSearchResults: VideoInfo[] = searchResults.data;
          newSearchResults.sort((a, b) => { // Move videos with the words in the title to the top of the results, both words -> mystery word -> user word
            const assignPriority = (title: string): number => {
              let hasMystery: boolean = title.includes(mysteryWord.toLowerCase());
              let hasUser: boolean = title.includes(userWord.toLowerCase());
              return (hasMystery && hasUser) ? 3 :
                (hasMystery) ? 2 :
                (hasUser) ? 1 : 0;
            }
            return assignPriority(b.title.toLowerCase()) - assignPriority(a.title.toLowerCase());
          });
          setSearchIndex(0);
          setVideosPurchased(0);
          setVideoId(newSearchResults[0].videoId);
          addToVideoHistory(newSearchResults[0]);
          currentSearch.current = newSearchResults;
          logEvent(analytics, 'search', { query: `${userWord} ${mysteryWord}`, results: newSearchResults});
        }
      })
      .catch((err: any) => setSearchErrorMessage(`There was an error executing the search, let's try a different search!`));
    };
    if (userWord && mysteryWord) searchVids();
  }, [userWord, mysteryWord]);

  /** Change video with previously fetched data when updating search index */
  useEffect(() => {
    if (currentSearch.current.length > 0 && currentSearch.current.length >= searchIndex) setVideoId(currentSearch.current[searchIndex].videoId); 
  }, [searchIndex]);
  
  /** End the game when the countdown timer reaches 0*/
  useEffect(() => {
    if (timeRemaining === 0) endGame();
  }, [timeRemaining]);

  // /** Update the background position based on the mouse position */
  // function handleMouseMove(event: React.MouseEvent): void {
  //   setBackgroundPosition([-.0015 * event.clientX, -0.004 * event.clientY]);
  // };

  /**
   * Starts a new game with given game mode and user word.
   * @param gameMode The chosen game mode.
   * @param initUserWord The initial user word to use in the game.
   * @returns void
   */
  function startGame(gameMode: GameMode, initUserWord: string): void {
    setVideoHistory([]);
    setCurrentScore(0);
    setGameMode(gameMode);
    changeUserWord(initUserWord, true);
    if (gameMode === 'endurance') {
      setHintPoints(maxHP);
    } else if (gameMode === 'speed') {
      setTimeRemaining(timeLimit);
    }
    startNextRound(true);
  }

  /**
   * Starts the next round of the game, generates a new mystery word, and resumes video playing.
   * @param newGame (optional) true if starting a new game
   * @returns void
   */
  function startNextRound(newGame?: boolean): void {
    setRoundNumber(newGame ? 0 : roundNumber+1);
    generateNewMysteryWord(true);
    setRoundScores({independence: 100, certainty: 100});
    setGameState('playing');
  }

  /**
   * Changes the gamestate to bring up the gameover screen, stops video playback.
   * @returns void
   */
  async function endGame() {
    setGameState('gameover');
    setVideoIsPlaying(false);
    setUserWord('');
  }

  /**
   * Returns the cost of using a hint in the game, optionally formatted as a string.
   * @param hintType - The type of hint to get the cost of, such as ‘correctGuess’, ‘changeWord’, etc.
   * @param formatCost - Whether to format the cost as a string or not. If true, the cost will be prefixed with ‘+’ or ‘-’ and suffixed with ‘HP’ or time units depending on the game mode. If false, the cost will be a number.
   * @returns The cost of using the hint, either as a number or a string. 
   */ 
  function getHintCost(hintType: keyof HintCosts): number;
  function getHintCost(hintType: keyof HintCosts, formatCost: true): string;
  function getHintCost(hintType: keyof HintCosts, formatCost?: boolean): number | string {
    const hintCosts: { [key in GameMode]: HintCosts } = { 
      endurance: {
        correctGuess: () => hintPoints >= maxHP - 5 ? 100 - hintPoints : 5,
        incorrectGuess: -2,
        changeWord: -10,
        nextVideo: -3,
        revealLetter: () => -1 * Math.floor(maxHP / (mysteryWord.length + 1)),
        newMysteryWord: () => Math.min(-1 * Math.floor(hintPoints / 2), -1),
      },
      speed: {
        correctGuess: () => 60000,
        incorrectGuess: 0,
        changeWord: -5000,
        nextVideo: 0,
        revealLetter: () => Math.min(-1000 * Math.floor(timeRemaining / mysteryWord.length / 500), -10000),
        newMysteryWord: () => -1000 * Math.floor(timeRemaining / 2 / 1000),
      },
    };
    let hintCost = hintCosts[gameMode][hintType];
    if (typeof hintCost === 'function') hintCost = hintCost();
    if (formatCost) {
      if (hintCost === 0) return ' FREE';
      return `${hintCost > 0 ? '+' : '-'}${gameMode === 'endurance' ? `${Math.abs(hintCost)} HP` : `${formatTime(Math.abs(hintCost))}`}`;
    } 
    return hintCost;
  }

  /**
   * Charges the user for the cost of a hint based on the current game mode and game state.
   * @param {keyof HintCosts} hintType - The key for the type of hint being used.
   * @returns {void}
   */ 
  function chargeHintCost(hintType: keyof HintCosts): void {
    // Deduct hint cost
    let hintCost: number = getHintCost(hintType);
    switch(gameMode) {
      case 'endurance': 
        const newHintPoints: number = Math.max(0, Math.min(hintPoints + hintCost, maxHP));
        setHintPoints(newHintPoints);
        if (newHintPoints === 0) endGame();
        break;
      case 'speed':
        setTimeRemaining(timeRemaining + hintCost);
        break;
      default:
        console.log(`Add a chargeHintCost case for ${gameMode}!`)
    }
    // Adjust round scores based on hint type
    if (hintType !== 'correctGuess') {
      const newRoundScores: RoundScores = roundScores;
      switch(hintType) {
        case 'incorrectGuess':
          newRoundScores.certainty-=5;
          break;
        case 'revealLetter':
          newRoundScores.independence-=Math.floor(100 / mysteryWord.length);
          break;
        default:
          newRoundScores.independence-=5;
      }
      setRoundScores(newRoundScores);
    }
  } 

  /**
   * Check if the current hint can be bought with remaining time or hint points
   * @param hintType 
   * @returns boolean - true if the hint can be afforded
   */
  function canAffordHint(hintType: keyof HintCosts): boolean {
    return (gameMode === 'speed' ? timeRemaining : hintPoints) > -1 * getHintCost(hintType);
  }

  /**
   * Checks if guessWord matches the mystery word. If guessWord matches, starts a new round and rewards hint points, if incorrect it charges hintpoints.
   * @param guessWord The word the user guessed.
   * @returns void if the guess is correct, otherwise returns "incorrect" for inputbutton animation.
   */
  function checkAnswer(guessWord: string): void | string {
    setGuessedWords([...guessedWords, guessWord]);
    if (mysteryWord.toLowerCase() === guessWord.toLowerCase()) {
      if (gameMode === 'speed') toggleTimer(false);
      setConfettiFalling(true);
      setVideoIsPlaying(false);
      setGameState('roundover');
      chargeHintCost('correctGuess');
      setCurrentScore(currentScore + roundScores.certainty + roundScores.independence);
      setAverageRoundScores({
        independence: (averageRoundScores.independence * roundNumber + roundScores.independence) / (roundNumber + 1), 
        certainty: (averageRoundScores.certainty * roundNumber + roundScores.certainty) / (roundNumber + 1),
      });
    } else {
      chargeHintCost('incorrectGuess');
      return 'incorrect';
    }
  }

  /**
   * Generates a new mystery word, sets the search index to 0, and charges hint points.
   * @param isFree if true, generates the new mystery word without a cost to hint points.
   * @returns void.
   */
  function generateNewMysteryWord(isFree?:boolean): void {
    setMysteryWord(wordsList.current[Math.floor(Math.random() * wordsList.current.length) + 1]);
    setGuessedWords([]);
    if (!isFree) chargeHintCost('newMysteryWord');
  }

  /**
   * Validates the user’s guess input for the mystery word.
   * @param input - The user’s input string.
   * @returns A string with an error message if the input is invalid, or null if the input is valid
   */
  function validateGuessMysteryWord(input: string): string | null {
    if (input.length !== mysteryWord.length) return `Must be ${mysteryWord.length} letters long! (${input.length}/${mysteryWord.length})`
    if (guessedWords.includes(input)) return `You already guessed ${input}!`;
    return null;
  }

  /**
   * Changes the user's word and charges hint points.
   * @param newWord The new user word.
   * @param isFree if true, changes the user's word without a cost to hint points.
   * @returns void.
   */
  function changeUserWord(newWord: string, isFree?: boolean): void {
    setUserWord(newWord);
    if (!isFree) chargeHintCost('changeWord');
    if (gameState==='roundover') startNextRound();
  }

  /**
   * Validates the user’s input for the new user word.
   * @param input - The user’s input string.
   * @returns A string with an error message if the input is invalid, or null if the input is valid
   */
  function validateChangeUserWord(input: string): string | null {
    if (input.length === 0) return `Enter your word!`;
    if (userWord.toLowerCase() === input.toLowerCase()) return `You're already using this word!`;
    const bannedWords: string[] = ['defin', 'dictio', 'dicta', 'prono', 'pronu', 'spell', 'meanin', 'translat', 'syno', 'anton', 'say'];
    if (bannedWords.some(word => input.toLowerCase().includes(word))) return 'That word is too easy!';
    return null;
  }
  
  /**
   * Reveals a letter in the mystery word and charges hint points 
   * @returns void.
   */
  function revealLetter(): void {
    mysteryWordComponentRef.current?.revealLetter(); 
    chargeHintCost('revealLetter');
  }

  /**
   * This function buys the next video, adds it to the video history, 
   * and sets the search index to purchased video, triggering it to play
   * @returns void
   */
  function buyNextVideo(): void {
    if (videosPurchased < currentSearch.current.length) {
      const newVideosPurchased = videosPurchased + 1
      setVideosPurchased(newVideosPurchased);
      setSearchIndex(newVideosPurchased);
      addToVideoHistory(currentSearch.current[newVideosPurchased]);
      chargeHintCost('nextVideo');
    }
  }

  /**
   * Adds a new VideoInfo object to the video history array for the current round.
   * @param {any} newVideoInfo - The youtube search result to add to the video history.
   * @returns {void}
   */
  function addToVideoHistory(newVideoInfo: VideoInfo): void {
    const newVideoHistory: VideoInfo[][] = [...videoHistory];
    (newVideoHistory[roundNumber] ||= []).push(newVideoInfo);
    setVideoHistory(newVideoHistory);
  }

  /**
   * Play video and resume the timer when the video is loaded
   * @returns void
   */
  function onVideoReady(): void {
    setVideoIsPlaying(true);
    setSearchInProgress(false);
    setVideoDuration(videoPlayerComponentRef.current?.getDuration());
    if (gameMode === 'speed') toggleTimer(true);
  }

  /**
   * Removes the last video from the video history and current search reslts if there is an error 
   * loading the video. It then sets the current video ID to the next video in the search results 
   * if there are any left. If there are no more videos left in the search results, it sets the 
   * current video ID to the previous video in the video history.
   * @param error - The error object.
   * @returns void
   */
  function handleVideoLoadError(error: any): void {
    if (gameMode==='speed') toggleTimer(false);
    let newVideoHistory: VideoInfo[][] = [...videoHistory];
    newVideoHistory[newVideoHistory.length - 1].splice(-1);
    setVideoHistory(newVideoHistory);
    currentSearch.current.splice(searchIndex, 1);
    if(currentSearch.current.length <= searchIndex) {
      // return to previous video if end of search results
      setSearchIndex(searchIndex - 1);
    } else {
      addToVideoHistory(currentSearch.current[searchIndex]);
    }
    setVideoId(currentSearch.current[searchIndex].videoId);
    console.log('error occurred on vid!', error);
  }

  return (
    <div onTouchMove={(e)=>e.preventDefault()}>
      <div className="app">
        <div className="tv-bezel">
          {gameState==='tutorial' && 
            <TutorialScreen/>
          }
          {gameState==='menu' && 
            <TitleScreen/>
          }
          {gameState==='roundover' && 
            <RoundOverScreen
              videoHistory={videoHistory}
              roundNumber={roundNumber}
              roundScores={roundScores}/>
          }
          {gameState==='gameover' && 
            <GameOverScreen
              finalScore={currentScore}
              videoHistory={videoHistory}
              gameMode={gameMode}
              averageRoundScores={averageRoundScores}/>
          }
          {searchInProgress &&
            <SearchingScreen
              userWord={userWord}
              mysteryWord={mysteryWord}
              errorMessage={searchErrorMessage}/>
          }
          {(gameState==='playing' && !searchInProgress) &&
            <SeekingOverlay
              videoDuration={videoDuration}
              getCurrentTime={videoPlayerComponentRef.current?.getCurrentTime}
              isPlaying={videoIsPlaying}
              onSeek={videoPlayerComponentRef.current?.seekTo}
              onPauseOrPlay={setVideoIsPlaying}/>
          }
        </div>
        <div className="video-player">
          <ReactPlayer
            ref={videoPlayerComponentRef}
            url={`https://www.youtube.com/embed/${videoId}`}
            width="100%"
            height="100%"
            pip={false}
            playing={videoIsPlaying}
            loop={true}
            config={{
              playerVars: { autoplay: 1 },
            }}
            onReady={onVideoReady}
            onError={handleVideoLoadError}
            position="absolute"/>
        </div>
        {gameState==='playing' && !searchInProgress &&
          <div className="video-selectors">
            <div className={`video-selector-container  ${searchIndex === 0 && 'hidden'}`}>
              <StandardButton 
                classNames="round"
                buttonText={`<`} 
                secondaryButtonText={`${searchIndex} / ${videosPurchased + 1}`}
                clickHandler={()=>setSearchIndex(Math.max(searchIndex - 1, 0))} />
              <div className="video-selector-label">
                {`${searchIndex} / ${videosPurchased + 1}`}
              </div>
            </div>
            <div className={`video-selector-container  ${searchIndex === videosPurchased && 'hidden'}`}>
              <StandardButton 
                classNames="round"
                buttonText={`>`} 
                secondaryButtonText={`${searchIndex + 2} / ${videosPurchased + 1}`}
                clickHandler={()=>setSearchIndex(Math.min(searchIndex + 1, videosPurchased))} />
                <div className="video-selector-label">
                  {`${searchIndex + 2} / ${videosPurchased + 1}`}
                </div>
            </div>
          </div>
        }
        {(gameState==='playing' || gameState==='roundover') &&
        <div className="score-board">
          <div className="hint-points">
            <div className='hint-points-label'>{gameMode === 'speed' ? 'Time Left:' : 'Hint Points:'}</div>
            <div className="hp-bar-container">
              {gameMode==='speed'
                ? <HPBar maxHP={timeLimit} currentHP={timeRemaining} isTimer={true}/>
                : <HPBar maxHP={maxHP} currentHP={hintPoints}/>
              }
            </div>
          </div>
          <span className="score">Score: <CountUp end={currentScore} preserveValue={true} duration={3} useEasing={false}/></span>
        </div>
        }
        {gameState==='playing' &&
          <div className={`words-container ${searchInProgress && 'hidden'}`}>
            <span>Search: 
              <span className="user-word">{userWord}</span>
              <MysteryWord ref={mysteryWordComponentRef} mysteryWord={mysteryWord}/>
            </span>
          </div>
        }
        {gameState==='gameover' &&
          <div className={`words-container`}>
            <span>Play Again?</span>
          </div>
        }
        <div className="hint-container">
          {(gameState==='menu') &&
            <div className="grid-container">
              <div className="full-row-item">
                <StandardButton
                  buttonText={`Play Endurance Mode`}
                  clickHandler={()=>{setGameMode('endurance'); setGameState('tutorial');}}/>
              </div>
              <div className="full-row-item">
                <StandardButton
                  buttonText={`Play Speed Mode`}
                  clickHandler={()=>{setGameMode('speed'); setGameState('tutorial');}}/>
              </div>
            </div>
          }
           {(gameState==='gameover') &&
            <div className="grid-container">
              <div className="full-row-item">
                <InputButton
                  buttonText={`Endurance Mode`}
                  clickHandler={(initUserWord)=>startGame('endurance', initUserWord)} 
                  placeholder={`Enter Your Word Here`}
                  validationFunction={validateChangeUserWord}/>
              </div>
              <div className="full-row-item">
                <InputButton
                  buttonText={`Speed Mode`}
                  clickHandler={(initUserWord)=>startGame('speed', initUserWord)}
                  placeholder={`Enter Your Word Here`}
                  validationFunction={validateChangeUserWord}/>
              </div>
              <div className="full-row-item">
                <StandardButton
                  buttonText={`Return to Title`}
                  clickHandler={()=>setGameState('menu')}/>
              </div>
            </div>
          }
          {(gameState==='tutorial') &&
            <div className="grid-container">
              <div className="full-row-item">
                <InputButton
                  buttonText={`Start Game`}
                  clickHandler={(initUserWord)=>startGame(gameMode, initUserWord)} 
                  placeholder={`Enter Your Word Here`}
                  validationFunction={validateChangeUserWord}/>
              </div>
              <div className="full-row-item">
                <StandardButton
                  buttonText={`Return to Title`}
                  clickHandler={()=>setGameState('menu')}/>
              </div>
            </div>
          }
          {(gameState==='playing' && !searchErrorMessage) &&
            <div className={`grid-container ${searchInProgress && 'disabled'}`}>
              <div className="grid-item">
                <InputButton
                  buttonText={`Guess Mystery Word`}
                  secondaryButtonText={`${getHintCost('incorrectGuess', true)} / ${getHintCost('correctGuess', true)}`}
                  clickHandler={checkAnswer} 
                  validationFunction={validateGuessMysteryWord}
                  placeholder={`${mysteryWord.length} Letter Word`}/>
              </div>
              <div className="grid-item">
                <InputButton 
                  classNames={`${!canAffordHint('changeWord') && 'disabled'}`} 
                  buttonText={`Change Your Word`} 
                  secondaryButtonText={getHintCost('changeWord', true)}
                  clickHandler={changeUserWord}
                  validationFunction={validateChangeUserWord}
                  placeholder={`Your New Word`}/>
              </div>
              <div className="grid-item">
                <StandardButton 
                  classNames={`${(videosPurchased >= currentSearch.current.length - 1 || !canAffordHint('nextVideo')) && 'disabled'}`} 
                  buttonText={`Buy Next Video`} 
                  secondaryButtonText={
                    videosPurchased >= currentSearch.current.length - 1 && currentSearch.current.length > 0 
                      ? 'All videos purchased!' 
                      : getHintCost('nextVideo', true)
                  }
                  clickHandler={buyNextVideo} />
              </div>
              <div className="grid-item">
                <StandardButton 
                  classNames={`${!canAffordHint('revealLetter') && 'disabled'}`} 
                  buttonText={`Reveal Random Letter`} 
                  secondaryButtonText={getHintCost('revealLetter', true)}
                  clickHandler={revealLetter} />
              </div>
              <div className="grid-item">
                <StandardButton 
                  classNames={`${!canAffordHint('newMysteryWord') && 'disabled'}`} 
                  buttonText={`New Mystery Word`} 
                  secondaryButtonText={getHintCost('newMysteryWord', true)}
                  clickHandler={()=>generateNewMysteryWord(false)} />
              </div>
              <div className="grid-item">
                <StandardButton 
                  buttonText={`Give Up`} 
                  secondaryButtonText={'Hold for 3 seconds'}
                  holdDownMode={true}
                  clickHandler={endGame} />
              </div>
            </div>
          }
          {(gameState==='roundover' || searchErrorMessage) &&
            <div className="grid-container">
              <div className="full-row-item">
                <InputButton
                  buttonText={`Choose Next Word`}
                  secondaryButtonText={`Change your word for free`}
                  clickHandler={(nextWord)=>changeUserWord(nextWord, true)}
                  validationFunction={validateChangeUserWord}
                  placeholder={`Current Word: ${userWord}`}/>
              </div>
              <div className="full-row-item">
                <div>OR</div>
              </div>
              <div className="full-row-item">
                <StandardButton
                  buttonText={`Let it Ride!`}
                  secondaryButtonText={`Keep using the same word "${userWord}"`}
                  clickHandler={searchErrorMessage ? ()=>generateNewMysteryWord(true) : startNextRound} />
              </div>
            </div>
          }
        </div>    
        {confettiFalling &&
          <Confetti
            className="confetti"
            width={width}
            height={height}
            frameRate={60}
            numberOfPieces={1000}
            tweenDuration={8000}
            recycle={false}
            onConfettiComplete={()=>setConfettiFalling(false)}
            colors={[ //REFERENCE: $rainbow-colors in variables.scss
              `rgb(252, 65, 65)`,
              `orange`,
              `yellow`,
              `rgb(33, 156, 33)`,
              `rgb(94, 94, 255)`,
              `rgb(163, 77, 224)`,
              `rgb(250, 178, 250)`
            ]}/>
        }   
      </div>
    </div>
  );
}

export default App;